import * as React from "react";
import { Button, Gapped, Modal } from "react-ui";
import { useEffect } from "react";

import { ValueEditorsFocusManager } from "../../../../Helpers/JumpByEnterOnNextControl/ValueEditorsFocusManager";
import { ParsedExtendedFiasField } from "../FiasInput/Types/FiasTypes";
import { FiasApi } from "../FiasInput/FiasApi";
import { FiasAddress } from "../Utils/FiasAddressUtils";
import { FnsOptions, MvdOptions } from "../FiasContainer";

import { FiasManualEditor } from "./FiasManualEditor";
import { FiasFnsManualEditor } from "./FiasCustomManualEditors/FiasFnsManualEditor/FiasFnsManualEditor";
import { FiasMvdManualEditor } from "./FiasCustomManualEditors/FiasMvdManualEditor/FiasMvdManualEditor";
import {
    extractMvdAddressFromModel,
    FiasMvdAddress,
    formatFiasMvdAddressToModel,
} from "./FiasCustomManualEditors/FiasMvdManualEditor/FiasMvdManualEditorUtils";

interface FiasModalProps {
    value: FiasAddress;
    onApply: (nextValue: FiasAddress) => void;
    onDiscard: () => void;
    stringAddress: string;
    focusByEnterOnNextControl: (event: React.KeyboardEvent) => void;
    title: string;
    withForeignCountry: boolean;
    withSteads: boolean;
    extendedFields: ParsedExtendedFiasField[];
    apiClient: FiasApi;
    fnsOptions?: FnsOptions;
    mvdOptions?: MvdOptions;
}

export function FiasModalEditor(props: FiasModalProps): JSX.Element {
    const [currentFiasAddress, setCurrentFiasAddress] = React.useState(props.value);
    const [currentFiasMvdAddress, setCurrentFiasMvdAddress] = React.useState<FiasMvdAddress>(
        extractMvdAddressFromModel(props.value)
    );

    useEffect(() => {
        ValueEditorsFocusManager.forceUpdateFocusedByEnterControls();
        return () => ValueEditorsFocusManager.forceUpdateFocusedByEnterControls();
    }, []);

    const handleChangeManualEditorValue = (nextValue: FiasAddress) => {
        setCurrentFiasAddress(nextValue);
    };

    const getFiasManualEditor = () => {
        if (props.fnsOptions?.extendModal) {
            return (
                <FiasFnsManualEditor
                    apiClient={props.apiClient}
                    withFederalTerritory={props.fnsOptions.withFederalTerritory}
                    withSteads={props.withSteads}
                    withIndex={props.fnsOptions.withIndex}
                    value={currentFiasAddress}
                    onValueChange={handleChangeManualEditorValue}
                    abbreviationSize={props.fnsOptions.abbreviationSize}
                    extendedFields={props.extendedFields}
                    hiddenFields={props.fnsOptions.hiddenFields}
                />
            );
        } else if (props.mvdOptions?.extendModal) {
            const onChange = (mvdAddress: FiasMvdAddress) => {
                setCurrentFiasMvdAddress(mvdAddress);
            };
            return <FiasMvdManualEditor mvdAddress={currentFiasMvdAddress} setMvdAddress={onChange} />;
        } else {
            return (
                <FiasManualEditor
                    value={currentFiasAddress}
                    extendedFields={props.extendedFields}
                    onValueChange={handleChangeManualEditorValue}
                    focusByEnterOnNextControl={props.focusByEnterOnNextControl}
                />
            );
        }
    };

    const onClose = () => {
        props.onDiscard();
    };

    const onSave = () => {
        if (props.mvdOptions?.extendModal) {
            props.onApply(formatFiasMvdAddressToModel(currentFiasMvdAddress));
        } else {
            props.onApply(currentFiasAddress);
        }
    };

    return (
        <Modal ignoreBackgroundClick={true} onClose={onClose}>
            <Modal.Header>{props.title}</Modal.Header>
            <Modal.Body>{getFiasManualEditor()}</Modal.Body>
            <Modal.Footer panel>
                <Gapped>
                    <Button size="medium" use="primary" data-tid="SaveButton" onClick={onSave}>
                        Сохранить
                    </Button>
                    <Button size="medium" data-tid="CancelButton" onClick={onClose}>
                        Отменить
                    </Button>
                </Gapped>
            </Modal.Footer>
        </Modal>
    );
}
